import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Star, Trash2, Check, X, Pin, PinOff } from 'lucide-react';
import '../styles/AdminReviews.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin dashboard for moderating reviews.
 * Auth: Emergent Google login.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AdminReviewsPage = () => {
  const location = useLocation();
  const initialUser = location.state?.user || null;

  const [authState, setAuthState] = useState(initialUser ? 'authenticated' : 'checking'); // checking | authenticated | unauthenticated
  const [user, setUser] = useState(initialUser);
  const [authError, setAuthError] = useState(null);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending'); // pending | approved | featured | all

  // Read ?error from URL once on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('error');
    if (err) {
      const message =
        err === 'network'
          ? 'Network error during sign-in. Try again.'
          : err;
      setAuthError(message);
    }
  }, [location.search]);

  // Check session unless we already have user from AuthCallback state
  useEffect(() => {
    if (initialUser) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) {
          if (!cancelled) setAuthState('unauthenticated');
          return;
        }
        const data = await r.json();
        if (!cancelled) {
          setUser(data);
          setAuthState('authenticated');
        }
      } catch {
        if (!cancelled) setAuthState('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/reviews`, { credentials: 'include' });
      if (r.status === 401 || r.status === 403) {
        setAuthState('unauthenticated');
        return;
      }
      const data = await r.json();
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authState === 'authenticated') fetchReviews();
  }, [authState, fetchReviews]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/reviews';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    setUser(null);
    setAuthState('unauthenticated');
    setReviews([]);
  };

  const patchReview = async (id, body) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/reviews/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('failed');
      const data = await r.json();
      setReviews((prev) => prev.map((rv) => (rv.id === id ? { ...rv, ...data.review } : rv)));
      toast.success('Review updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteReview = async (id, name) => {
    if (!window.confirm(`Delete review from ${name}? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/reviews/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error('failed');
      setReviews((prev) => prev.filter((rv) => rv.id !== id));
      toast.success('Review deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const filtered = reviews.filter((r) => {
    if (filter === 'pending') return !r.approved;
    if (filter === 'approved') return r.approved;
    if (filter === 'featured') return r.featured;
    return true;
  });

  // -------------- RENDER --------------
  if (authState === 'checking') {
    return <div className="admin-loading" data-testid="admin-loading">Checking session…</div>;
  }

  if (authState === 'unauthenticated') {
    return (
      <div className="admin-login-wrap" data-testid="admin-login-wrap">
        <div className="admin-login-card">
          <h1 className="admin-login-title">AfriCasa Admin</h1>
          <p className="admin-login-sub">
            Sign in with the AfriCasa Google account to manage reviews.
          </p>
          {authError && (
            <p data-testid="admin-auth-error" style={{ color: '#e88080', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              {authError}
            </p>
          )}
          <button onClick={login} className="admin-login-btn" data-testid="admin-google-login-btn">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  const counts = {
    pending: reviews.filter((r) => !r.approved).length,
    approved: reviews.filter((r) => r.approved).length,
    featured: reviews.filter((r) => r.featured).length,
    all: reviews.length,
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <header className="admin-header">
          <h1 className="admin-title" data-testid="admin-page-title">Reviews Dashboard</h1>
          <div className="admin-user-strip" data-testid="admin-user-strip">
            {user?.picture && <img src={user.picture} alt="" className="admin-user-pic" />}
            <span className="admin-user-email">{user?.email}</span>
            <button onClick={logout} className="admin-logout-btn" data-testid="admin-logout-btn">
              <LogOut size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Logout
            </button>
          </div>
        </header>

        <div className="admin-filter-bar" role="tablist">
          {['pending', 'approved', 'featured', 'all'].map((f) => (
            <button
              key={f}
              className={`admin-filter-chip ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              role="tab"
              aria-selected={filter === f}
            >
              {f} ({counts[f]})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="admin-empty">Loading reviews…</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty" data-testid="admin-empty-state">No reviews in this view.</div>
        ) : (
          <div className="admin-reviews-grid" data-testid="admin-reviews-grid">
            {filtered.map((r) => (
              <article
                key={r.id}
                className={`admin-review-card ${!r.approved ? 'pending' : ''} ${r.featured ? 'featured' : ''}`}
                data-testid={`admin-review-${r.id}`}
              >
                {r.photo_url ? (
                  <img src={r.photo_url} alt={r.name} className="admin-review-photo" />
                ) : (
                  <div className="admin-review-photo-placeholder" aria-hidden="true">
                    {(r.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="admin-review-body">
                  <div className="admin-review-meta">
                    <span className="admin-review-name">{r.name}</span>
                    {r.organization && <span className="admin-review-org">· {r.organization}</span>}
                    <span className="admin-review-stars" aria-label={`${r.rating} stars`}>
                      {'★'.repeat(r.rating || 0)}{'☆'.repeat(Math.max(0, 5 - (r.rating || 0)))}
                    </span>
                    {!r.approved && <span className="admin-badge pending">Pending</span>}
                    {r.approved && <span className="admin-badge approved">Approved</span>}
                    {r.featured && <span className="admin-badge featured">★ Featured</span>}
                  </div>
                  <p className="admin-review-text">{r.reviewText}</p>
                  <div className="admin-review-date">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                  </div>
                </div>

                <div className="admin-review-actions">
                  {!r.approved ? (
                    <button
                      className="admin-btn admin-btn-primary"
                      onClick={() => patchReview(r.id, { approved: true })}
                      data-testid={`admin-approve-${r.id}`}
                    >
                      <Check size={12} /> Approve
                    </button>
                  ) : (
                    <button
                      className="admin-btn"
                      onClick={() => patchReview(r.id, { approved: false })}
                      data-testid={`admin-unapprove-${r.id}`}
                    >
                      <X size={12} /> Un-publish
                    </button>
                  )}

                  <button
                    className="admin-btn"
                    onClick={() => patchReview(r.id, { featured: !r.featured })}
                    data-testid={`admin-feature-${r.id}`}
                    disabled={!r.approved}
                    title={!r.approved ? 'Approve before featuring' : ''}
                  >
                    {r.featured ? <><PinOff size={12} /> Unfeature</> : <><Pin size={12} /> Feature</>}
                  </button>

                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => deleteReview(r.id, r.name)}
                    data-testid={`admin-delete-${r.id}`}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewsPage;
